import { getCanonicalSiteLocale, getConfiguredSiteDomains } from 'lib/utils/domain-locale-utils';
import { mergedSites } from 'lib/site-resolver/build-sites';
import { SitecorePageProps } from 'lib/page-props';

const LOCALE_PREFIX_REGEX = /^\/([a-z]{2}-[a-z]{2})(?=\/|$)/i;

export type ErrorPageSiteEntry = {
  siteName: string;
  locale: string;
};

const errorPagePropsCache = new Map<string, SitecorePageProps | null>();

export function getBuildErrorPageSiteEntries(
  fallbackLocale: string,
  defaultSite?: string,
  includeGeneralMillsWildcard = false
): ErrorPageSiteEntry[] {
  const wildcardSite = process.env.SITECORE_SITE_NAME_WILDCARD?.trim();
  if (/^generalmills-\*$/i.test(wildcardSite || '')) {
    if (!includeGeneralMillsWildcard) {
      return [];
    }

    return getErrorPageSiteEntries(fallbackLocale).filter((entry) =>
      /^GeneralMills-/i.test(entry.siteName)
    );
  }

  const buildSite = process.env.SITECORE_SITE_NAME?.trim() || defaultSite?.trim();
  if (!buildSite) {
    return [];
  }

  return getErrorPageSiteEntries(fallbackLocale).filter((entry) => entry.siteName === buildSite);
}

export function getRequestedRouteLocale(
  routeLocale?: string,
  path?: string,
  fallbackLocale?: string
): string {
  if (routeLocale?.trim()) {
    return routeLocale;
  }

  const localeMatch = path?.match(LOCALE_PREFIX_REGEX);
  if (localeMatch?.[1]) {
    return localeMatch[1];
  }

  return fallbackLocale || '';
}

export function getErrorPageSiteKey(options: {
  siteName: string;
  resolvedSiteLocale?: string;
  routeLocale?: string;
  path?: string;
}): string {
  const requestedLocale = getRequestedRouteLocale(
    options.routeLocale,
    options.path,
    options.resolvedSiteLocale
  );
  const locale = getCanonicalSiteLocale(
    options.siteName,
    requestedLocale,
    options.resolvedSiteLocale
  );

  return `${options.siteName}__${locale}`;
}

export function getErrorPageSiteEntries(fallbackLocale: string): ErrorPageSiteEntry[] {
  const entries = new Map<string, ErrorPageSiteEntry>();

  mergedSites.forEach((site) => {
    const locale = site.language || fallbackLocale;
    entries.set(`${site.name}__${locale.toLowerCase()}`, {
      siteName: site.name,
      locale,
    });
  });

  getConfiguredSiteDomains().forEach((siteDomain) => {
    const locales = [siteDomain.defaultLocale, ...(siteDomain.locales ?? [])].filter(
      (value): value is string => !!value
    );

    locales.forEach((locale) => {
      entries.set(`${siteDomain.siteName}__${locale.toLowerCase()}`, {
        siteName: siteDomain.siteName,
        locale,
      });
    });
  });

  return [...entries.values()];
}

export async function fetchErrorPageProps(options: {
  errorType: '404' | '500';
  siteName: string;
  locale: string;
}): Promise<SitecorePageProps | null> {
  const cacheKey = `${options.errorType}__${options.siteName}__${options.locale.toLowerCase()}`;
  if (errorPagePropsCache.has(cacheKey)) {
    return errorPagePropsCache.get(cacheKey) ?? null;
  }

  const params = new URLSearchParams({
    type: options.errorType,
    site: options.siteName,
    locale: options.locale,
  });
  const response = await fetch(`/api/error/page?${params.toString()}`);

  if (!response.ok) {
    return null;
  }

  const payload = (await response.json()) as { pageProps?: SitecorePageProps | null };
  const pageProps = payload.pageProps ?? null;
  if (pageProps) {
    errorPagePropsCache.set(cacheKey, pageProps);
  }
  return pageProps;
}

export function getCachedErrorPageProps(options: {
  errorType: '404' | '500';
  siteName: string;
  locale: string;
}): SitecorePageProps | null | undefined {
  const cacheKey = `${options.errorType}__${options.siteName}__${options.locale.toLowerCase()}`;
  return errorPagePropsCache.get(cacheKey);
}
